import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Phone, ShieldCheck } from "lucide-react";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "closed"];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLeads = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
      setError("");
    } catch {
      setToken("");
      sessionStorage.removeItem("admin_token");
      setError("Session expired. Please sign in again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed.");
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setLeads(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-charcoal grain flex items-center justify-center px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-ivory p-10 border border-border">
          <ShieldCheck className="h-8 w-8 text-wine mb-5" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold text-charcoal">Admin Access</h1>
          <p className="text-sm text-stone2 mt-2 mb-7">{SITE.companyName} — leads dashboard</p>
          <label className="block">
            <span className="block text-[11px] font-bold tracking-[0.2em] uppercase text-stone2 mb-1">Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-border focus:border-wine outline-none py-3 text-base text-charcoal rounded-none"
              placeholder="Admin password"
            />
          </label>
          {error && <p data-testid="admin-login-error" className="text-wine text-sm mt-3">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full mt-7 bg-wine text-ivory py-3.5 text-[12px] font-bold tracking-[0.14em] uppercase hover:bg-wine-light transition-colors disabled:opacity-60 inline-flex justify-center items-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="bg-charcoal text-ivory border-b border-ivory/10">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <div>
            <p className="font-display text-2xl font-semibold">{SITE.companyName}</p>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-gold">Inspection Requests</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-refresh-btn" onClick={() => fetchLeads(token)} className="inline-flex items-center gap-2 border border-ivory/25 px-4 py-2.5 text-[11px] font-bold tracking-[0.12em] uppercase hover:bg-ivory hover:text-charcoal transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 border border-ivory/25 px-4 py-2.5 text-[11px] font-bold tracking-[0.12em] uppercase hover:bg-wine transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 py-10">
        {loading && !leads ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-wine" /></div>
        ) : !leads || leads.length === 0 ? (
          <p data-testid="admin-empty" className="text-center text-stone2 py-20 font-display text-2xl italic">No inspection requests yet.</p>
        ) : (
          <div className="space-y-4">
            <p data-testid="admin-lead-count" className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone2">{leads.length} lead{leads.length !== 1 ? "s" : ""}</p>
            {leads.map((l) => (
              <div key={l.id} data-testid={`admin-lead-${l.id}`} className="border border-border bg-white p-6 flex flex-col lg:flex-row lg:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-display text-xl font-semibold text-charcoal">{l.name}</p>
                    <span className={`text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 ${l.status === "new" ? "bg-wine text-ivory" : l.status === "contacted" ? "bg-gold/20 text-charcoal" : "bg-sand text-stone2"}`}>
                      {l.status}
                    </span>
                  </div>
                  <p className="text-sm text-stone2 mt-1">{l.pest} · {l.location} · {l.callback_time}</p>
                  <p className="text-[11px] text-stone2/70 mt-1">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
                <a data-testid={`admin-call-${l.id}`} href={`tel:+91${l.phone.replace(/\D/g, "").slice(-10)}`} className="inline-flex items-center gap-2 border border-charcoal/25 px-4 py-2.5 text-[12px] font-bold text-charcoal hover:border-wine hover:text-wine transition-colors shrink-0">
                  <Phone className="h-3.5 w-3.5" /> {l.phone}
                </a>
                <select
                  data-testid={`admin-status-${l.id}`}
                  value={l.status}
                  onChange={(e) => setStatus(l.id, e.target.value)}
                  className="border border-border bg-ivory px-3 py-2.5 text-[12px] font-semibold text-charcoal rounded-none shrink-0"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
